import { useEffect } from "react";
import { Link, useLocation, useParams } from "react-router";
import type { PortalConfig } from "../config";
import type { Lookups } from "../types";
import { EstimateDraftProvider } from "./ticket/estimateDraft";
import { Header } from "./ticket/Header";
import { NotesCard, PaymentCard, ReturnsCard, ShippingLabelCard } from "./ticket/LeftColumn";
import { ExpensesCard, ServicesCard, TouchesCard } from "./ticket/MiddleColumn";
import { EstimateCard, MessagesCard, PublishedEstimatesCard } from "./ticket/RightColumn";
import { CustomerCard, ReturnLabelCard, SummaryCard } from "./ticket/TopRow";
import { TicketContext, useTicket } from "./ticket/useTicket";
import { WatchesCard } from "./ticket/Watches";

/** One ticket, laid out like the owner's mockup (Nov 3). */
export function TicketPage({ config, lookups }: { config: PortalConfig; lookups: Lookups | null }) {
  const { ticketNo = "" } = useParams();
  const { detail, loadError, problem, setProblem, notice, setNotice, saving, save, reload } = useTicket(config, ticketNo);
  // Add Ticket opens the new ticket with what happened (label, email, text).
  const location = useLocation();
  const created = (location.state as { notice?: string } | null)?.notice;
  useEffect(() => { if (created) setNotice(created); }, [created, setNotice]);

  if (loadError) return <div className="tp"><p><Link to="/tickets">← Tickets</Link></p><p className="error">{loadError}</p></div>;
  if (!detail || String(detail.ticket.ticketNo) !== ticketNo) return <div className="tp"><p className="muted">Loading ticket…</p></div>;

  return (
    <TicketContext.Provider value={{ config, lookups, detail, save, saving, reload, notify: setNotice }}>
      <EstimateDraftProvider>
        <div className="tp">
          <Header />
          {problem && (
            <div className={problem.conflict ? "problem conflict" : "problem"} role="alert">
              <span>{problem.text}</span>
              {problem.conflict && <button type="button" className="btn outline" onClick={reload}>Reload ticket</button>}
              <button type="button" className="link-btn" onClick={() => setProblem(null)}>Dismiss</button>
            </div>
          )}
          {notice && (
            <div className="notice" role="status">
              <span>{notice}</span>
              <button type="button" className="link-btn" onClick={() => setNotice(null)}>Dismiss</button>
            </div>
          )}
          <div className="tp-top">
            <SummaryCard /><CustomerCard /><ReturnLabelCard /><NotesCard />
          </div>
          <div className="tp-cols">
            <div className="col"><WatchesCard /><PaymentCard /><ShippingLabelCard /><ReturnsCard /></div>
            {/* One wide column (owner, 2026-11-03): Services, Expenses, Estimate, Published Estimates, then Messages and Touches side by side. */}
            <div className="col"><ServicesCard /><ExpensesCard /><EstimateCard /><PublishedEstimatesCard />
              <div className="tp-pair"><MessagesCard /><TouchesCard /></div></div>
          </div>
        </div>
      </EstimateDraftProvider>
    </TicketContext.Provider>
  );
}
